// src/features/feedback/components/FeedbackManagementDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close,
  Settings,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeedbackManagement, useFeedbackStatus } from '../hooks/useFeedbackManagement';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

export default function FeedbackManagementDialog({ open, onClose, feedback, onSuccess }) {
  const { t } = useTranslation(['feedback', 'common']);
  const [activeTab, setActiveTab] = useState(0);
  
  // State
  const [visibility, setVisibility] = useState('');
  const [status, setStatus] = useState('');
  const [adminResponse, setAdminResponse] = useState('');

  // Hooks
  const { updateVisibility, updateStatus, updating, error, clearError } = useFeedbackManagement();
  const { VISIBILITY_STATUS, IMPLEMENTATION_STATUS } = useFeedbackStatus();

  // Initialize form with current feedback values
  useEffect(() => {
    if (feedback && open) {
      setVisibility(feedback.visibility);
      setStatus(feedback.status);
      setAdminResponse(feedback.admin_response || '');
    }
  }, [feedback, open]);

  // Clear error when user makes changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [visibility, status, adminResponse]);

  const handleClose = () => {
    if (!updating) {
      setActiveTab(0);
      clearError();
      onClose();
    }
  };

  const handleVisibilitySubmit = async () => {
    const result = await updateVisibility(feedback.feedback_id, visibility);
    if (result.success) {
      if (onSuccess) onSuccess();
      handleClose();
    }
  };

  const handleStatusSubmit = async () => {
    const data = {
      status,
      admin_response: adminResponse.trim() || null,
    };
    
    const result = await updateStatus(feedback.feedback_id, data);
    if (result.success) {
      if (onSuccess) onSuccess();
      handleClose();
    }
  };

  const visibilityChanged = visibility !== feedback?.visibility;
  const statusChanged = status !== feedback?.status || 
    (adminResponse.trim() || null) !== (feedback?.admin_response || null);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={updating}
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Settings color="primary" />
            <Box>
              <Typography variant="h6">
                {t('feedback:management.title', 'Manage Feedback')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {feedback?.title}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={updating}
            edge="end"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} centered>
          <Tab label={t('feedback:management.visibility_tab', 'Visibility')} />
          <Tab label={t('feedback:management.status_tab', 'Status & Response')} />
        </Tabs>
      </Box>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        {/* Visibility Tab */}
        <TabPanel value={activeTab} index={0}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="feedback-management-dialog-visibility-label">{t('feedback:management.visibility', 'Visibility')}</InputLabel>
            <Select labelId="feedback-management-dialog-visibility-label"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              disabled={updating}
              label={t('feedback:management.visibility', 'Visibility')}
            >
              <MenuItem value="NOT_DECIDED">
                {t('feedback:status.visibility.not_decided', 'Not Decided')}
              </MenuItem>
              <MenuItem value="PUBLISHED">
                {t('feedback:status.visibility.published', 'Published')}
              </MenuItem>
              <MenuItem value="HIDDEN">
                {t('feedback:status.visibility.hidden', 'Hidden')}
              </MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>{t('feedback:management.visibility_info_title', 'Visibility Options:')}</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              • <strong>{t('feedback:status.visibility.published', 'Published')}:</strong> {t('feedback:management.published_desc', 'Visible to everyone')}
              <br />
              • <strong>{t('feedback:status.visibility.hidden', 'Hidden')}:</strong> {t('feedback:management.hidden_desc', 'Only visible to admins and submitter')}
              <br />
              • <strong>{t('feedback:status.visibility.not_decided', 'Not Decided')}:</strong> {t('feedback:management.not_decided_desc', 'Pending decision')}
            </Typography>
          </Alert>
        </TabPanel>

        {/* Status & Response Tab */}
        <TabPanel value={activeTab} index={1}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="feedback-management-dialog-status-label">{t('feedback:management.status', 'Status')}</InputLabel>
            <Select labelId="feedback-management-dialog-status-label"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={updating}
              label={t('feedback:management.status', 'Status')}
            >
              <MenuItem value="NOT_DECIDED">
                {t('feedback:status.implementation.not_decided', 'Not Decided')}
              </MenuItem>
              <MenuItem value="PENDING_REVIEW">
                {t('feedback:status.implementation.pending_review', 'Pending Review')}
              </MenuItem>
              <MenuItem value="IMPLEMENTED">
                {t('feedback:status.implementation.implemented', 'Implemented')}
              </MenuItem>
              <MenuItem value="WILL_NOT_IMPLEMENT">
                {t('feedback:status.implementation.will_not_implement', 'Will Not Implement')}
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('feedback:management.admin_response', 'Admin Response (Optional)')}
            placeholder={t('feedback:management.response_placeholder', 'Provide feedback to the submitter...')}
            variant="outlined"
            margin="normal"
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            disabled={updating}
            multiline
            rows={4}
            helperText={t('feedback:management.response_helper', 'This message will be visible to the submitter')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              {t('feedback:management.status_info', 'The status helps track the implementation progress of this feedback.')}
            </Typography>
          </Alert>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={updating}
          color="inherit"
        >
          {t('common:buttons.cancel', 'Cancel')}
        </Button>
        
        {activeTab === 0 && (
          <Button
            onClick={handleVisibilitySubmit}
            variant="contained"
            disabled={updating || !visibilityChanged}
            startIcon={updating ? <CircularProgress size={16} /> : null}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {updating
              ? t('feedback:management.updating', 'Updating...')
              : t('feedback:management.update_visibility', 'Update Visibility')
            }
          </Button>
        )}

        {activeTab === 1 && (
          <Button
            onClick={handleStatusSubmit}
            variant="contained"
            disabled={updating || !statusChanged}
            startIcon={updating ? <CircularProgress size={16} /> : null}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {updating
              ? t('feedback:management.updating', 'Updating...')
              : t('feedback:management.update_status', 'Update Status')
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}